import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircleIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleSolidIcon } from '@heroicons/react/24/solid';
import { ArrowRightIcon } from '@heroicons/react/20/solid';
import { Card, Button } from '../ui';
import { CHECKLIST_ITEMS } from '../onboarding/GettingStartedChecklist';
import { HELP_BUCKETS } from '../help/helpCMS';
import { onboardingAPI } from '../../services/api';

// TODO: PT-50-F1 — Replace localStorage checklist with server-derived state.
// localStorage is browser-local: cross-device users see stale/wrong completion.
// Failure mode: contractor completes setup on laptop, opens dashboard on desktop
// → sees every item unchecked. Actively misinforms; undermines State B's purpose.
// Minimum fix: fetch getCompanyProfile() + getBranding() in State B only,
// derive 4 of 8 items from truth. See plan § 50.4 for full derivation table.

/**
 * Curated articles for new-user dashboard (SA-3 / F-7).
 * First article from each of the 4 most relevant buckets for a new user.
 */
const FIRST_RUN_ARTICLES = [
  { id: 'art-quote-01', title: 'Creating a Quote', bucketId: 'quotes' },
  { id: 'art-cust-01', title: 'Adding and Managing Customers', bucketId: 'customers' },
  { id: 'art-jobs-01', title: 'Job Lifecycle Overview', bucketId: 'jobs_wo' },
  { id: 'art-set-01', title: 'Company Settings and Onboarding', bucketId: 'settings' },
];

/**
 * DashboardFirstRun — State B of the dashboard.
 * Shown when the tenant has zero quotes, jobs, and invoices.
 * Renders welcome message, inline checklist, and helpful guide cards.
 * IDI-659: canStartSetup (an admin of a truly empty dealer) reads the
 * dealer's setup flag; until it is finished, a "Set up your account" card
 * opens /onboarding. Unreadable flag → no card.
 */
export default function DashboardFirstRun({ canStartSetup = false }) {
  const navigate = useNavigate();
  const [showSetupCard, setShowSetupCard] = useState(false);

  useEffect(() => {
    if (!canStartSetup) { setShowSetupCard(false); return undefined; }
    let cancelled = false;
    onboardingAPI.getState()
      .then((state) => {
        const finished = !!state && (state.completed === true || state.status === 'completed');
        if (!cancelled) setShowSetupCard(!!state && !finished);
      })
      .catch(() => { if (!cancelled) setShowSetupCard(false); });
    return () => { cancelled = true; };
  }, [canStartSetup]);

  const [completedItems, setCompletedItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('bidit_checklist_completed') || '[]');
    } catch { return []; }
  });

  useEffect(() => {
    localStorage.setItem('bidit_checklist_completed', JSON.stringify(completedItems));
  }, [completedItems]);

  const toggleItem = (key) => {
    setCompletedItems(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  const completedCount = completedItems.length;
  const totalCount = CHECKLIST_ITEMS.length;

  return (
    <div className="space-y-6">
      {/* Welcome header */}
      <div className="text-center py-6">
        <h2 className="text-2xl font-bold text-gray-900">Welcome to BIDIT</h2>
        <p className="text-gray-500 mt-1">{showSetupCard ? "Here's what to do next." : "You're all set up. Here's what to do next."}</p>
      </div>

      {showSetupCard && (
        <Card>
          <div className="p-5 flex items-center justify-between gap-4" data-testid="setup-card">
            <div>
              <h3 className="text-base font-semibold text-gray-900">Set up your account</h3>
              <p className="text-sm text-gray-500 mt-0.5">Add your company, margins, tax and team.</p>
            </div>
            <Button onClick={() => navigate('/onboarding')}>Start setup</Button>
          </div>
        </Card>
      )}

      {/* Two-column layout: checklist + guides */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Checklist card */}
        <Card>
          <div className="p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-gray-900">Finish setting up</h3>
              <span className="text-xs text-gray-500">{completedCount} of {totalCount}</span>
            </div>

            {/* Progress bar — uses discrete width classes since there are only 8 items */}
            <div className="w-full bg-gray-200 rounded-full h-1.5 mb-4 overflow-hidden">
              <div className={`bg-green-500 h-1.5 rounded-full transition-all ${
                completedCount === 0 ? 'w-0' :
                completedCount === 1 ? 'w-[12.5%]' :
                completedCount === 2 ? 'w-1/4' :
                completedCount === 3 ? 'w-[37.5%]' :
                completedCount === 4 ? 'w-1/2' :
                completedCount === 5 ? 'w-[62.5%]' :
                completedCount === 6 ? 'w-3/4' :
                completedCount === 7 ? 'w-[87.5%]' : 'w-full'
              }`} />
            </div>

            {/* Checklist items */}
            <ul className="space-y-2">
              {CHECKLIST_ITEMS.map(item => {
                const isDone = completedItems.includes(item.key);
                return (
                  <li
                    key={item.key}
                    className={`flex items-start gap-3 p-3 rounded-md border cursor-pointer transition-all ${
                      isDone
                        ? 'bg-green-50 border-[var(--color-border)]'
                        : 'bg-white border-[var(--color-border)] hover:border-primary-200 hover:bg-primary-50/30'
                    }`}
                    onClick={() => {
                      if (!isDone) {
                        navigate(item.path);
                      }
                    }}
                  >
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); toggleItem(item.key); }}
                      className="mt-0.5 flex-shrink-0"
                    >
                      {isDone ? (
                        <CheckCircleSolidIcon className="h-5 w-5 text-green-500" />
                      ) : (
                        <CheckCircleIcon className="h-5 w-5 text-gray-300" />
                      )}
                    </button>
                    <div>
                      <p className={`text-sm font-medium ${isDone ? 'text-gray-500 line-through' : 'text-gray-800'}`}>
                        {item.label}
                      </p>
                      <p className="text-xs text-gray-400">{item.description}</p>
                    </div>
                  </li>
                );
              })}
            </ul>

            {/* All-done message */}
            {completedCount === totalCount && (
              <div className="mt-4 p-3 bg-green-50 rounded-md text-center">
                <p className="text-sm font-medium text-green-700">All done! You're ready to go.</p>
              </div>
            )}
          </div>
        </Card>

        {/* Helpful guides card */}
        <Card>
          <div className="p-5">
            <h3 className="text-base font-semibold text-gray-900 mb-4">Helpful guides</h3>
            <ul className="space-y-3">
              {FIRST_RUN_ARTICLES.map(article => {
                const bucket = HELP_BUCKETS.find(b => b.id === article.bucketId);
                return (
                  <li key={article.id}>
                    <button
                      type="button"
                      onClick={() => navigate(`/help/article/${article.id}`)}
                      className="w-full text-left flex items-center justify-between p-3 rounded-md border border-[var(--color-border)] hover:border-primary-200 hover:bg-primary-50/30 transition-all"
                    >
                      <div>
                        <p className="text-sm font-medium text-gray-800">{article.title}</p>
                        {bucket && (
                          <p className="text-xs text-gray-400 mt-0.5">{bucket.label}</p>
                        )}
                      </div>
                      <ArrowRightIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        </Card>
      </div>
    </div>
  );
}
